/**
 * Main App Component
 * Root of the dashboard application
 * UI/UX Enhancement: Improved background with subtle gradient, smooth transitions
 */

import React, { useMemo, useState, Suspense, lazy } from 'react';
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
} from '@mui/material';
import { lightTheme, darkTheme } from './theme';
import { PreferencesProvider, usePreferences } from './context/PreferencesContext';
import ErrorBoundary from './components/ErrorBoundary';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { tokens } from './design-system';

import { 
  DashboardPageSkeleton, 
  AnalyticsPageSkeleton, 
  IncidentsPageSkeleton 
} from './components/PageSkeletons';
import { SkipLink } from './components/SkipLink';

// Lazy load pages for better bundle size
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Analytics = lazy(() => import('./pages/Analytics').then(m => ({ default: m.Analytics })));
const Incidents = lazy(() => import('./pages/Incidents').then(m => ({ default: m.Incidents })));

export type PageType = 'dashboard' | 'incidents' | 'analytics';

// Page-specific skeleton selector
const getPageSkeleton = (page: PageType) => {
  switch (page) {
    case 'analytics':
      return <AnalyticsPageSkeleton />;
    case 'incidents':
      return <IncidentsPageSkeleton />;
    case 'dashboard':
    default:
      return <DashboardPageSkeleton />;
  }
};

// Inner component that uses preferences context
const AppContent: React.FC = () => {
  const { preferences } = usePreferences();
  const prefersDarkMode = preferences.darkMode;
  const theme = useMemo(() => (prefersDarkMode ? darkTheme : lightTheme), [prefersDarkMode]);
  const [currentPage, setCurrentPage] = useState<PageType>('dashboard');

  const renderPage = () => {
    switch (currentPage) {
      case 'incidents':
        return <Incidents />;
      case 'analytics':
        return <Analytics />;
      case 'dashboard':
      default:
        return <Dashboard />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          bgcolor: prefersDarkMode ? tokens.colors.dark.bg : '#f8fafc',
          // Subtle gradient background for premium feel
          background: prefersDarkMode 
            ? `linear-gradient(180deg, ${tokens.colors.dark.bg} 0%, ${tokens.colors.dark.surface} 100%)`
            : `linear-gradient(180deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)`,
          backgroundAttachment: 'fixed',
          transition: `background ${tokens.motion.duration.slow}ms ${tokens.motion.easing.easeInOut}`,
        }}
      >
        {/* Skip to content link for keyboard accessibility */}
        <SkipLink targetId="main-content" />
        
        <Header currentPage={currentPage} onPageChange={setCurrentPage} />

        <Container 
          component="main"
          id="main-content"
          maxWidth="lg"
          sx={{ 
            py: 4, 
            flex: 1,
          }}
        >
          <Suspense fallback={getPageSkeleton(currentPage)}>
            {renderPage()}
          </Suspense>
        </Container>

        <Footer />
      </Box>
    </ThemeProvider>
  );
};

// Outer wrapper with provider and error boundary
export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <PreferencesProvider>
        <AppContent />
      </PreferencesProvider>
    </ErrorBoundary>
  );
};

export default App;
